import React, { useState } from "react";
import SoftTypography from "../SoftTypography";
import SoftBox from "../SoftBox";
import SoftButton from "../SoftButton";
import { useTheme, Collapse } from "@mui/material";
import { GET_CONNECTION_URL, DISABLE_INTEGRATION } from "../../api/integration";
import { GOOGLE_BUSINESS } from "../../constants/integrations";
import { GET_ZAPIER_AUTH_URL, GET_USER as GET_ZAPIER_USER } from "../../api/integrations/zapier";
import { UseSelectedWorkspace } from "../../hooks/company";
import { UseCompanyId } from "../../hooks/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
// import { updateWorkspaceKpi } from "../../redux/kpi/actions";
import { useWorkspaceKpiUpdate } from "../../hooks/kpi";

import { GET_IMAGE } from "../../helpers/images";
import AppSwitch from "../AppSwitch";
import { startCase } from "lodash";
import toast from "react-hot-toast";
import { toErrorMessage, toDisplayText } from "../../helpers/errorText";
import Dialog from "@mui/material/Dialog";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import LinkOffRoundedIcon from "@mui/icons-material/LinkOffRounded";
import AppSettings from "../WorkspaceKpi/apps/AppSettings";
import ConfirmationModal from "../Modals/ConfirmationModal";

// Every GBP location under one profile shares the same business name, so the
// address is what actually tells the locations apart.
const getLocationLabel = (instance) =>
  instance?.address?.trim() ||
  instance?.businessName?.trim() ||
  (instance?.locationId ? `Location ${instance.locationId}` : "");

const IntegrationApp = ({
  key,
  integrationId,
  currentIntegrations,
  name,
  code,
  logo,
  integrations,
  parameters,
  isConnected,
  connectedInstances,
  description,
  config,
  maxWorkspaceKpis,
  setAdding,
  groupKpis,
}) => {
  const theme = useTheme();
  const {
    primary: { main: primaryColor },
  } = theme.palette;
  const [showApp, setShowApp] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const { _id: groupId } = UseSelectedWorkspace();
  const companyId = UseCompanyId();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { updateWorkspaceKpi } = useWorkspaceKpiUpdate();

  // GBP is the only app that supports connecting more than one account
  // (one per business location) per company.
  const isMultiInstance = code === GOOGLE_BUSINESS;
  let connectButtonLabel = "Connect";
  if (isMultiInstance) {
    connectButtonLabel = "Add Location";
  } else if (isConnected) {
    connectButtonLabel = "Connected";
  }

  const appLabel = toDisplayText(name, "integration");

  const handleDisconnectInstance = (instance) => {
    const disconnecting = toast.loading("Disconnecting...");
    DISABLE_INTEGRATION({ integrationId: instance._id, operation: "disable" })
      .then(() => {
        toast.success(
          `Disconnected ${getLocationLabel(instance) || "location"}.`,
          { id: disconnecting },
        );
        window.location.reload();
      })
      .catch((err) => {
        toast.error(toErrorMessage(err.response?.data, "Server error"), {
          id: disconnecting,
        });
      });
  };

  const handleConnect = () => {
    if (!isConnected || isMultiInstance) {
      if (config.isOauth || config.isApideck) {
        const connecting = toast.loading("Initializing connection ...");
        const params = config.isApideck
          ? { unifiedApi: config.unifiedApi, serviceId: config.serviceId }
          : null;

        GET_CONNECTION_URL(code, params)
          .then(({ data: url }) => {
            if (url) {
              toast.success("Connecting...", { id: connecting });
              const windowRef = window.open("", "_self");
              windowRef.location.href = url;
            } else {
              toast.error("Unable to establish connection.", {
                id: connecting,
              });
            }
          })
          .catch((err) => {
            toast.error(toErrorMessage(err.response?.data, "Server error"), {
              id: connecting,
            });
          });
      } else if (config.isZapier) {
        // Only redirect to Zapier if not already connected (user connects once)
        GET_ZAPIER_USER()
          .then((res) => {
            if (res?.data && !res.data.tokenExpired) {
              toast.success("Already connected to Zapier.");
              return;
            }
            window.location.href = GET_ZAPIER_AUTH_URL(companyId);
          })
          .catch(() => {
            window.location.href = GET_ZAPIER_AUTH_URL(companyId);
          });
      } else {
        setShowSettings(true);
      }
    }
  };

  const handleSelect = (activated, integration) => {
    if (activated && (groupKpis?.filter(kpi => !kpi.isDraft)?.length ?? 0) < maxWorkspaceKpis) {
      if (isConnected) {
        const integrationMeta = integration.isZapier
          ? {
              id: integration.id,
              code,
              status: "disconnected",
            }
          : null;

          updateWorkspaceKpi(groupId, {
            // Use integrationId assigned to specific KPI integration; else use the general app's integrationId
            integrationId: integration.integrationId ?? integrationId,
            kpiModelId: integration.kpiModelId,
            type: integration.type,
            sourceId: integration.sourceId,
            ...(integrationMeta && { integrationMeta }),
          });

        if (integration.isZapier) {
          navigate(`/zapier-setup?id=${integration.id}`);
        }
      } else {
        handleConnect();
      }
    } else {
      toast.error("Max numbers of KPIs reached.");
      setAdding(false);
    }
  };

  return (
    <SoftBox
      sx={{
        display: "flex",
        borderRadius: "16px",
        background: "white",
        border: "1px solid #E9ECEF",
        boxShadow: " 0px 2px 3px 0px rgba(0, 0, 0, 0.05)",
        width: "100%",
        overflow: "hidden",
        flexDirection: "column",
        padding: { mini: "14px", sm: "24px" },
        height: "100%",
      }}
    >
      <SoftBox
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          alignItems: "start",
          zIndex: 3,
        }}
      >
        <SoftBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            gap: "8px",
          }}
        >
          <SoftBox
            sx={{
              // Fixed size + flexShrink: 0 — without this the logo and the
              // Connect/Connected button were both regular (shrinkable) flex
              // children, so on a narrow 2-up mobile grid the row overflowed
              // and the browser crushed the logo box down to a sliver to make
              // room for the button's text, instead of shrinking the button.
              width: { mini: "44px", sm: "56px" },
              height: { mini: "44px", sm: "56px" },
              flexShrink: 0,
              background: "#fff",
              borderRadius: 2,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              border: "1px solid #E9ECEF",
              boxShadow: "0px 2px 3px 0px rgba(0, 0, 0, 0.05)",
              padding: "8px",
            }}
          >
            {logo ? (
              <img src={GET_IMAGE(logo)} alt="" className="w-full" />
            ) : (
              <SoftTypography variant="h3" sx={{ color: "primary.main" }}>
                {name && name[0]}
              </SoftTypography>
            )}
          </SoftBox>
          {code !== "manual" && !(config.isZapier && isConnected) && (
            <SoftBox
              sx={{ display: "flex", gap: "0.5rem", alignItems: "center", flexShrink: 0 }}
            >
              {isConnected && !isMultiInstance ? (
                // Connected, single-instance apps had no way to disconnect from
                // here at all — the pill just sat there reading "Connected"
                // with a no-op click. Hover (or focus, for keyboard users)
                // flips it over to reveal the real action underneath, like a
                // status pill turning into a switch the moment you reach for it.
                // Confirm before disable — accidental Disconnect stops sync
                // (Hub shows Connect again) even though the API key is kept.
                <ConfirmationModal
                  title={`Disconnect ${appLabel}?`}
                  question={`Metrics that sync from ${appLabel} will stop updating until you connect again. This is easy to undo, but only do this if you mean to.`}
                  yesText="Disconnect"
                  noText="Cancel"
                  color="error"
                  onSuccess={() =>
                    handleDisconnectInstance(connectedInstances?.[0])
                  }
                >
                  <SoftButton
                    variant="outlined"
                    aria-label={`Disconnect ${appLabel}`}
                    title={`Disconnect ${appLabel}`}
                    sx={{
                      position: "relative",
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      minWidth: { mini: "96px", sm: "128px" },
                      padding: { mini: "4px 10px", sm: "6px 16px" },
                      fontSize: { mini: "11px", sm: "14px" },
                      border: `1px solid ${primaryColor}`,
                      color: primaryColor,
                      transition:
                        "border-color 220ms cubic-bezier(0.16,1,0.3,1), background-color 220ms cubic-bezier(0.16,1,0.3,1)",
                      "&:hover, &:focus-visible": {
                        borderColor: "#DC3545",
                        backgroundColor: "rgba(220, 53, 69, 0.06)",
                      },
                      "& .ia-flip": {
                        display: "grid",
                        gridTemplateAreas: '"stack"',
                        perspective: "260px",
                        width: "100%",
                      },
                      "& .ia-face": {
                        gridArea: "stack",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "5px",
                        backfaceVisibility: "hidden",
                        transformStyle: "preserve-3d",
                        transition:
                          "transform 260ms cubic-bezier(0.16,1,0.3,1), opacity 200ms ease",
                      },
                      "& .ia-face--front": {
                        transform: "rotateX(0deg)",
                        opacity: 1,
                      },
                      "& .ia-face--back": {
                        transform: "rotateX(90deg)",
                        opacity: 0,
                        color: "#DC3545",
                      },
                      "&:hover .ia-face--front, &:focus-visible .ia-face--front": {
                        transform: "rotateX(-90deg)",
                        opacity: 0,
                      },
                      "&:hover .ia-face--back, &:focus-visible .ia-face--back": {
                        transform: "rotateX(0deg)",
                        opacity: 1,
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        "& .ia-face": {
                          transform: "none",
                          transition: "opacity 150ms ease",
                        },
                        "&:hover .ia-face--front, &:focus-visible .ia-face--front": {
                          transform: "none",
                        },
                        "&:hover .ia-face--back, &:focus-visible .ia-face--back": {
                          transform: "none",
                        },
                      },
                    }}
                  >
                    <span className="ia-flip">
                      <span className="ia-face ia-face--front">
                        <CheckRoundedIcon sx={{ fontSize: "1rem !important" }} />
                        Connected
                      </span>
                      <span className="ia-face ia-face--back">
                        <LinkOffRoundedIcon sx={{ fontSize: "1rem !important" }} />
                        Disconnect
                      </span>
                    </span>
                  </SoftButton>
                </ConfirmationModal>
              ) : (
                <SoftButton
                  sx={{
                    border: "1px solid rgba(0, 0, 0, 0.2)",
                    color: "rgba(0, 0, 0, 0.4)",
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    minWidth: "auto",
                    padding: { mini: "4px 10px", sm: "6px 16px" },
                    fontSize: { mini: "11px", sm: "14px" },
                    "&:hover": {
                      borderColor: "rgba(0, 0, 0, 0.4)",
                    },
                  }}
                  variant="outlined"
                  onClick={handleConnect}
                >
                  {connectButtonLabel}
                </SoftButton>
              )}
            </SoftBox>
          )}
        </SoftBox>
        <SoftTypography
          variant="h5"
          color="black"
          sx={{ fontSize: { mini: "0.95rem", xxs: "1.1rem", lg: "1.25rem" } }}
        >
          {toDisplayText(name, "Integration")}
        </SoftTypography>
        <SoftTypography
          variant="button"
          sx={{ color: "#6C757D", fontWeight: 400, lineHeight: 1.25 }}
        >
          {toDisplayText(description, "")}
        </SoftTypography>

        {isMultiInstance && connectedInstances?.length > 0 && (
          <SoftBox
            sx={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}
          >
            {connectedInstances.map((instance) => (
              <SoftBox
                key={instance._id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: "8px",
                  border: "1px solid #E9ECEF",
                  borderRadius: "8px",
                  padding: "4px 6px 4px 10px",
                }}
              >
                <SoftTypography
                  variant="button"
                  sx={{ color: "#343A40", fontWeight: 400, fontSize: "0.8rem" }}
                >
                  {getLocationLabel(instance) || "Connected location"}
                </SoftTypography>
                <ConfirmationModal
                  title={`Disconnect ${getLocationLabel(instance) || "this location"}?`}
                  question={`Metrics that sync from this ${appLabel} location will stop updating until you connect again.`}
                  yesText="Disconnect"
                  noText="Cancel"
                  color="error"
                  onSuccess={() => handleDisconnectInstance(instance)}
                >
                  <SoftButton
                    variant="text"
                    sx={{
                      color: "#DC3545",
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "2px 8px",
                      fontSize: "0.75rem",
                    }}
                  >
                    Disconnect
                  </SoftButton>
                </ConfirmationModal>
              </SoftBox>
            ))}
          </SoftBox>
        )}
      </SoftBox>

      <SoftBox flex={1} display="flex" justifyContent="end" marginTop="1rem">
        {!config.isApideck && (
          <Dialog open={showSettings} onClose={() => setShowSettings(false)}>
            <SoftBox className="w-[620px] p-8">
              <AppSettings
                integrationId={integrationId}
                name={name}
                code={code}
                logo={logo}
                parameters={parameters}
                handleClose={() => setShowSettings(false)}
              />
            </SoftBox>
          </Dialog>
        )}

        <Collapse
          in={showApp}
          timeout="auto"
          unmountOnExit
          sx={{ marginTop: "0.5rem" }}
        >
          {integrations?.map((integration) => (
            <SoftBox
              key={integration.description ?? integration.type}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingX: 2,
              }}
            >
              <SoftBox
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  px: 1,
                }}
              >
                <SoftBox sx={{ maxWidth: "25px" }}>
                  {logo ? (
                    <img
                      src={GET_IMAGE(logo)}
                      alt={name}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <SoftTypography variant="h3" sx={{ color: "primary.main" }}>
                      {name && name[0]}
                    </SoftTypography>
                  )}
                </SoftBox>
                <SoftTypography sx={{ color: "#000000" }} variant="body2">
                  {integration.description ||
                    `${startCase(integration.type)} KPI`}
                </SoftTypography>
              </SoftBox>
              <SoftBox sx={{ mr: 2 }}>
                <AppSwitch
                  onChange={(e) => handleSelect(e.target.checked, integration)}
                />
              </SoftBox>
            </SoftBox>
          ))}
        </Collapse>
      </SoftBox>
    </SoftBox>
  );
};

export default IntegrationApp;
